import {useRef, useState} from 'react';
import {Box, Flex, IconButton, Image, Spinner} from '@chakra-ui/react';
import {Star} from 'lucide-react';
import {DriveFile, isImageFile} from '../drive/types';
import {formatDisplayDateTime, parseFileName} from '../domain/naming';
import {displayedDimensions, formatAspectRatioShort} from '../domain/aspectRatio';
import {useThumbnail} from '../imageCache/useThumbnail';
import {useNearViewport} from '../hooks/useNearViewport';
import {useLongPress} from '../hooks/useLongPress';

interface FileCardProps {
    file: DriveFile;
    selected: boolean;
    onSelect: (e: React.MouseEvent) => void;
    onOpen: () => void;
    /** Plazma star; omitted outside the web bin. */
    star?: FileCardStar;
    /** The album's cover image (info.json `cover`) — outlined in red. */
    isCover?: boolean;
    onContextMenu?: (e: React.MouseEvent) => void;
}

export interface FileCardStar {
    starred: boolean;
    isPending: boolean;
    isDisabled: boolean;
    onToggle: () => void;
}

function FileGlyph() {
    return (
        <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor" aria-hidden="true">
            <path d="M6 2c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13z"/>
        </svg>
    );
}

export function FileCard({file, selected, onSelect, onOpen, star, isCover, onContextMenu}: FileCardProps) {
    const parsed = parseFileName(file.name);
    const label = parsed ? formatDisplayDateTime(parsed.date) : file.name;
    const suffix = parsed?.suffixNumber ? ` (${parsed.suffixNumber})` : '';
    const photographer = parsed?.photographerCode ?? '';
    const dimensions = displayedDimensions(file);
    // Right of the photographer; in the title row when the name has no photographer (outside the convention).
    const aspectRatioLabel = dimensions &&
        <Box flexShrink={0} color="gray.500" fontSize="sm" title="Képarány">{formatAspectRatioShort(dimensions)}</Box>;

    const containerRef = useRef<HTMLDivElement>(null);
    const longPress = useLongPress(onContextMenu);
    const isNearViewport = useNearViewport(containerRef);
    const [primaryFailed, setPrimaryFailed] = useState(false);

    const isImage = isImageFile(file);

    // Fast path: Drive's own resized thumbnail, loaded as a plain <img> (no fetch — sidesteps
    // CORS entirely). Fall back to the slower-but-reliable EXIF-embedded preview only if that
    // fails to load (usually because of rate limiting)
    let {url: thumbnailUrl, isLoading: fallbackLoading} = useThumbnail(
        file.id,
        file.thumbnailLink,
        isNearViewport && isImage,
        primaryFailed
    );

    const [isLoading, setIsLoading] = useState(true);

    // If it's not an image, try loading the drive generated thumbnail. Show file glyph if unavailable.
    const displayUrl = isImage ? thumbnailUrl : primaryFailed ? undefined : file.thumbnailLink;
    const showSpinner = isNearViewport && fallbackLoading;

    return (
        <Box
            ref={containerRef}
            position="relative"
            borderWidth="2px"
            borderRadius="lg"
            overflow="hidden"
            cursor="pointer"
            // Selection keeps its blue border; a selected cover also gets a red outer ring.
            borderColor={selected ? 'blue.400' : isCover ? 'red.500' : 'gray.200'}
            boxShadow={selected && isCover ? '0 0 0 2px var(--chakra-colors-red-500)' : undefined}
            title={isCover ? 'Borítókép' : undefined}
            onClick={onSelect}
            onDoubleClick={(e) => {
                // A Ctrl/Cmd double click is two selection toggles, not an open.
                if (e.ctrlKey || e.metaKey) return;
                if (isImage || file.name === 'info.json') onOpen();
            }}
            {...longPress}
            // No iOS callout (image/link menu) on a long press: it opens our menu instead.
            css={{WebkitTouchCallout: 'none'}}
        >
            <Box
                position="relative"
                bg="gray.100"
                color="gray.400"
                aspectRatio={3 / 2}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                {displayUrl
                    ? <>
                        <Image
                            src={displayUrl}
                            alt={file.name}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                            onLoad={() => setIsLoading(false)}
                            onError={() => setPrimaryFailed(true)}
                            referrerPolicy="no-referrer"
                        />
                        {isLoading &&
                            <Box
                                position="absolute"
                                inset={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bg="gray.100"
                            >
                                <Spinner size="sm" color="gray.400"/>
                            </Box>
                        }
                    </>
                    : showSpinner
                        ? <Spinner size="sm" color="gray.400"/>
                        : <FileGlyph/>
                }
                {star &&
                    // The wrapper keeps clicks on the star — even while it's disabled — from
                    // also selecting or opening the photo.
                    <Box
                        position="absolute"
                        top={1.5}
                        right={1.5}
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                    >
                        <IconButton
                            aria-label={star.starred ? 'Eltávolítás a plazmából' : 'Plazmába másolás'}
                            title={star.starred ? 'Plazmában — kattints az eltávolításhoz' : 'Plazmába másolás'}
                            aria-pressed={star.starred}
                            size="sm"
                            variant="ghost"
                            borderRadius="full"
                            bg="blackAlpha.500"
                            color={star.starred ? 'yellow.300' : 'white'}
                            _hover={{bg: 'blackAlpha.700'}}
                            loading={star.isPending}
                            disabled={star.isDisabled}
                            onClick={star.onToggle}
                        >
                            <Star size={18} fill={star.starred ? 'currentColor' : 'none'}/>
                        </IconButton>
                    </Box>
                }
            </Box>
            <Box p="0 10px 8px 10px">
                <Flex mt="1" justify="space-between" align="baseline" gap={2}>
                    <Box fontWeight="semibold" as="h5" lineHeight="tight" lineClamp={1}>
                        {label+suffix}
                    </Box>
                    {!parsed && aspectRatioLabel}
                </Flex>
                <Flex color="gray.500" fontSize="sm" justify="space-between" gap={2}>
                    <Box lineClamp={1}>{photographer}</Box>
                    {parsed && aspectRatioLabel}
                </Flex>
            </Box>
        </Box>
    );
}
